import { TextareaAutosize, TextareaAutosizeProps } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { makeStyles } from "@saleor/macaw-ui";
import cls from "classnames";
import React, { useEffect, useState } from "react";

interface CountTextFieldProps extends TextareaAutosizeProps {
  maxLength?: number;
  lineLimit?: number;
  title?: any | string;
  value?: string;
  name: string;
  placeHolder?: string;
  onChange: (event: any) => void;
  className?: any;
  viewOnly?: boolean;
  zoomable?: boolean;
  minWidth?: number | string;
  verticalResizingOnly?: boolean;
  allowDecrease?: boolean;
  id?: string;
}

const useStyles = makeStyles(
  theme => ({
    fieldZone: {
      "&:hover": {
        border: "solid 1px",
        borderColor: theme.palette.info.main,
        borderRadius: 4,
        boxShadow: "0px 0px 0px 3px rgba(1, 178, 237, 0.08)"
      },
      "&:focus-within": {
        borderColor: theme.palette.info.main,
        borderRadius: 4,
        boxShadow: "0px 0px 0px 3px rgba(1, 178, 237, 0.24)"
      },
      background: "#FFF",
      padding: "10px 10px 0 10px",
      border: "solid 1px",
      borderColor: theme.palette.grey[400],
      borderRadius: 4
    },
    fieldView: {
      background: "#FFF",
      padding: "10px 10px 0 10px"
    },
    title: {
      marginBottom: 10,
      color: theme.palette.info.dark,
      display: "flex",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        marginRight: 10
      }
    },
    inputField: {
      width: "100%",
      maxWidth: "100%",
      minWidth: "100%",
      color: theme.palette.grey[900],
      border: 0,
      overflow: "auto !important",
      fontSize: "1.4rem",
      "&:focus-visible": {
        outline: "none"
      },
      "&::placeholder": {
        color: theme.palette.grey[500],
        fontSize: "1.4rem"
      }
    },
    viewPre: {
      whiteSpace: "pre-line"
    }
  }),
  { name: "CountTextField" }
);

const TextAreaField: React.FC<CountTextFieldProps> = ({
  lineLimit,
  maxLength,
  rowsMax,
  rowsMin,
  title,
  value = "",
  name,
  onChange,
  onBlur,
  placeHolder,
  className,
  viewOnly,
  zoomable,
  minWidth,
  verticalResizingOnly,
  allowDecrease = true,
  id = "",
  ...agrs
}) => {
  const classes = useStyles({ rowsMax: Number(rowsMax) });
  const [textValue, setValue] = useState(value);
  const [isFocus, setFocus] = useState(false);

  const handleChange = newValue => {
    const textField = newValue.target.value;
    const textWithOutBreakLine = textField.replaceAll(/\n/g, "");
    if (maxLength) {
      if (textWithOutBreakLine.length > maxLength) {
        if (!(allowDecrease && textField.length < value.length)) {
          return;
        }
      }
    }
    if (lineLimit && textField.length) {
      const toLineArr = textField.split("\n");
      const isFullLine = toLineArr.some(line => line.length > lineLimit);
      if (isFullLine) {
        if (newValue.key !== "Enter") {
          return;
        }
      }
    }
    if (rowsMax) {
      const toLineArr = textField.split("\n");
      if (toLineArr.length > rowsMax) {
        if (textField.length >= value.length) {
          if (!(allowDecrease && textField.length < value.length)) {
            return;
          }
        }
      }
    }
    setValue(textField);
    onChange({
      target: {
        value: textField,
        name
      }
    });
  };

  useEffect(() => {
    setValue(value);
  }, [value]);

  const handleFocus = () => {
    setFocus(true);
  };

  const handleOnBlur = e => {
    setFocus(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyDown = e => {
    const preValue = e.target.value;
    if (lineLimit) {
      const toLineArr = preValue.split("\n");
      const isFullLine = toLineArr.some(line => line.length > lineLimit);
      if (isFullLine) {
        if (e.key !== "Enter") {
          // e.preventDefault();
        }
      }
    }
  };

  return (
    <div className={cls(className)}>
      <div
        className={cls({
          [classes.fieldZone]: !viewOnly,
          [classes.fieldView]: viewOnly
        })}
      >
        {title ? <div className={classes.title}>{title}</div> : null}
        {!viewOnly ? (
          <TextareaAutosize
            {...agrs}
            value={textValue}
            onChange={handleChange}
            className={classes.inputField}
            rowsMin={rowsMin || 2}
            placeholder={!isFocus ? placeHolder || "พิมพ์ที่นี่..." : ""}
            id={id}
            name={name}
            style={{
              minWidth: minWidth && zoomable && minWidth,
              resize: verticalResizingOnly ? "vertical" : "both"
            }}
            onFocus={handleFocus}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
          />
        ) : (
          ""
        )}
        {viewOnly ? (
          <MyTypography variant={"body3"} className={classes.viewPre}>
            {textValue}
          </MyTypography>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default TextAreaField;
